import { CloudFunctionHttpError } from '../../services/cloud-functions-errors';

/** How long the coach screen waits between tries when the server answers HTTP 500 (#6113).
 *
 * Right after a client's intake is filled in, the server can refuse to make the invite link,
 * because the intake has not reached its database yet. It answers HTTP 500, and the app cannot
 * tell that refusal from other server errors, because only the status reaches it. The call is
 * safe to repeat: the server reuses a token it already made. So an HTTP 500 gets several
 * tries, about 20 seconds in total. The old single retry after 3 seconds was refused as well
 * in release-line CI run 36860123361, and a call 32 seconds later succeeded. */
export const CLAIM_LINK_SERVER_ERROR_DELAYS_MS = [2000, 3000, 5000, 10000] as const;

/** Any other error (a dropped connection, an expired session, a 502 or 503) keeps the old single
 * retry after 3 seconds, so a real failure is shown without a 20-second wait. A 502 or 503 has
 * already been retried once by the cloud-functions service, which also reports it to Sentry, so
 * giving it the long schedule as well would multiply both the calls and the reports. */
export const CLAIM_LINK_OTHER_ERROR_DELAYS_MS = [3000] as const;

/** How long the coach screen waits for its own writes to be uploaded before the first call (#6792).
 * A timeout is not a failure: the call is made anyway and the schedules above take over. */
export const CLAIM_LINK_UPLOAD_WAIT_MS = 15000;

export interface ClaimLinkRetryOptions {
  sleepAsync: (ms: number) => Promise<void>;
  /** true once the page has been closed: stop trying and answer `undefined` */
  isCancelled: () => boolean;
  /** awaited once before the first call, so the server reads the slot the page just saved (#6792).
   * The server reads its own database, and a write the page just made locally may not be there yet.
   * Its answer is ignored: the retry schedule above still covers an upload that is slower still. */
  waitForLocalWritesUploadedAsync?: () => Promise<unknown>;
}

/** true for an HTTP 500, which is how the server's "intake not complete yet" refusal arrives */
function isServerRefusal(err: unknown): boolean {
  return err instanceof CloudFunctionHttpError && err.httpStatus === 500;
}

/** Call `callAsync` until it answers, retrying on the schedule above. Throws the last error
 * when the schedule is used up. Answers `undefined` when the page was closed while waiting. */
export async function fetchClaimLinkWithRetryAsync<T>(callAsync: () => Promise<T>, options: ClaimLinkRetryOptions): Promise<T | undefined> {
  if (options.waitForLocalWritesUploadedAsync) {
    await options.waitForLocalWritesUploadedAsync();
    if (options.isCancelled()) return undefined;
  }
  let retriesDone = 0;
  for (;;) {
    try {
      return await callAsync();
    } catch (err) {
      const delays = isServerRefusal(err) ? CLAIM_LINK_SERVER_ERROR_DELAYS_MS : CLAIM_LINK_OTHER_ERROR_DELAYS_MS;
      // if this kind of error has used up its tries, the caller shows it
      if (retriesDone >= delays.length) throw err;
      console.log(`fetchClaimLink failed, retry ${retriesDone + 1}/${delays.length} in ${delays[retriesDone] / 1000}s:`, err);
      await options.sleepAsync(delays[retriesDone]);
      retriesDone++;
      if (options.isCancelled()) return undefined;
    }
  }
}
